<template>
  <div ref="strategyRoot">
    <div class="section-header-simple">
      <div class="section-title-group">
        <van-icon name="manager-o" size="16" color="#fff" style="margin-right: 4px;" />
        <span>{{ $t('nba_suggestions') }}</span>
      </div>
      <div class="section-header-actions">
        <span v-if="displayGeneratedTimeText" class="suggestions-generated-time-summary">{{ displayGeneratedTimeText }}</span>
        <button
          v-if="suggestionList.length > 0"
          type="button"
          class="refresh-btn section-refresh-btn"
          :class="{ 'is-refreshing': refreshing || activeNbaSuggestionJobRunning }"
          :disabled="loading || refreshing || activeNbaSuggestionJobRunning"
          @click="confirmRefreshSuggestions"
        >
          <van-icon name="replay" size="12" class="refresh-btn-icon" />
          <span class="refresh-btn-text">{{ $t('refresh_batch') }}</span>
        </button>
      </div>
    </div>
    <div class="section-card" style="margin-top: 0;">
      <div v-if="isSuggestionLoading && displaySuggestionList.length === 0" class="loading-wrapper">
        <div class="loading-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
      <div v-else-if="displaySuggestionList.length === 0" class="nba-empty-state">
        {{ $t('no_recommended_nba') }}
      </div>
      <div v-else>
        <div class="strategy-carousel-shell">
          <button
            v-if="displaySuggestionList.length > 1 && activeSuggestionIndex > 0"
            type="button"
            class="strategy-nav-btn strategy-nav-prev"
            aria-label="Previous suggestion"
            @click.stop="goToAdjacentSuggestion(-1)"
          >
            <span class="strategy-nav-icon">
              <van-icon name="arrow-left" size="18" />
            </span>
          </button>

          <transition-group
            ref="strategyCarousel"
            name="strategy-card-transition"
            tag="div"
            class="strategy-carousel"
            @scroll.native.passive="handleStrategyCarouselScroll"
          >
            <div
              v-for="(doc, idx) in displaySuggestionList"
              :key="getSuggestionKey(doc, idx)"
              class="strategy-card"
              :class="{ 'is-generating-preview': isGeneratingPreviewSuggestion(doc) }"
            >
              <div class="system-recommendation-banner">
                <van-icon name="star" size="8" color="#d9f7e6" style="margin-right: 4px; opacity: 0.6; margin-top: -8px;" />
                <van-icon name="star" size="12" color="#d9f7e6" style="margin-right: 10px; opacity: 0.8;" />
                <span class="banner-text">{{ $t('suggested_nba') }}</span>
                <van-icon name="star" size="12" color="#d9f7e6" style="margin-left: 10px; opacity: 0.8;" />
                <van-icon name="star" size="8" color="#d9f7e6" style="margin-left: 4px; opacity: 0.6; margin-top: -8px;" />
                <span class="banner-count">{{ $t('suggestion_position', { current: idx + 1, total: displaySuggestionList.length }) }}</span>
              </div>

              <div class="card-top-row">
                <div class="doctor-info-simple" @click="handleSuggestionDoctorClick(doc)">
                  <div class="avatar-wrapper">
                    <img :src="defaultAvatar" class="avatar-img" />
                  </div>
                  <div class="info-text">
                    <div class="name">{{ doc.name | displayPhysicianName }}</div>
                    <div class="details">
                      <div class="dept">{{ doc.department }} {{ doc.title }}</div>
                      <div class="hospital">{{ doc.hospital }}</div>
                    </div>
                  </div>
                </div>
                <div class="nba-meta-pills">
                  <span v-if="doc.suggestion_type" class="nba-action-pill" :class="getActionTypeClass(doc.suggestion_type)">
                    <font-awesome-icon :icon="getActionTypeIcon(doc.suggestion_type)" class="nba-pill-icon" />
                    <span>{{ getActionTypeLabel(doc.suggestion_type) }}</span>
                  </span>
                  <span v-if="doc.product_name" class="nba-product-pill">{{ doc.product_name | displayProductName }}</span>
                  <span v-if="doc.is_key_customer" class="nba-customer-pill">{{ getCustomerLevelLabel(doc) }}</span>
                </div>
              </div>

              <div class="info-content-box">
                <div v-if="hasProductContext(doc)" class="product-context-row">
                  <span class="product-context-label">{{ $t('ai_nba_product_label') }}</span>
                  <span v-if="doc.product_name" class="product-context-value">{{ doc.product_name | displayProductName }}</span>
                  <span v-if="doc.physician_segment" class="product-context-tag">{{ doc.physician_segment }}</span>
                  <span v-if="getProductSalesSignalLabel(doc.product_sales_signal)" class="product-context-tag is-signal">{{ getProductSalesSignalLabel(doc.product_sales_signal) }}</span>
                </div>
                <div class="info-row-flex">
                  <div class="info-panel last-visit-panel">
                    <div class="info-panel-label">
                      <van-icon name="clock-o" color="#00b852" />
                      <span>{{ $t('last_visit_suffix') }}</span>
                    </div>
                    <div v-if="doc.days_since_last_call || doc.days_since_last_call === 0" class="info-panel-value">
                      {{ getDaysText(doc.days_since_last_call) }}
                    </div>
                    <div v-else class="info-panel-value no-visit-record">
                      {{ $t('no_visit_record') }}
                    </div>
                  </div>
                  <div class="info-panel reason-panel">
                    <div class="info-panel-label">
                      <van-icon name="todo-list-o" color="#00b852" />
                      <span>{{ $t('recommend_reason') }}</span>
                      <span v-if="getTriggerTypeLabel(doc.trigger_type)" class="reason-trigger-tag">{{ getTriggerTypeLabel(doc.trigger_type) }}</span>
                    </div>
                    <div class="info-panel-value reason">
                      <span>{{ (doc.suggestion_reason || '--') | displayProductText }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="isContentPushSuggestion(doc)" class="content-push-box">
                <div class="content-push-label">
                  <font-awesome-icon :icon="['fas', 'newspaper']" />
                  <span>{{ $t('recommended_content') }}</span>
                  <button
                    v-if="getContentPushContentId(doc) && !isGeneratingPreviewSuggestion(doc)"
                    type="button"
                    class="content-preview-btn"
                    @click.stop="previewContentSuggestion(doc)"
                  >
                    <van-icon name="eye-o" />
                    <span>{{ $t('task_preview') }}</span>
                  </button>
                </div>
                <div class="content-push-title">
                  {{ getContentPushPrimaryText(doc) }}
                </div>
              </div>

              <div v-if="!isGeneratingPreviewSuggestion(doc)" class="accept-action-row">
                <button type="button" class="ai-helper-btn ai-helper-inline-btn" @click="$emit('ask-ai-helper', getAiHelperPayload(doc))">
                  <div class="ai-gradient-text">
                    <span>{{ getAiHelperLabel(doc) }}</span>
                  </div>
                </button>
                <button
                  type="button"
                  class="accept-plan-btn"
                  :class="getPrimaryActionClass(doc)"
                  @click="handlePrimarySuggestionAction(doc)"
                >
                  <font-awesome-icon :icon="getPrimaryActionIcon(doc)" class="accept-plan-btn-icon" />
                  <span>{{ getPrimaryActionLabel(doc) }}</span>
                </button>
              </div>

              <div v-if="!isGeneratingPreviewSuggestion(doc)" class="card-footer">
                <div class="footer-btn" @click="markAsVisited(idx)">
                  <font-awesome-icon :icon="alreadyDoneIcon" class="footer-btn-icon" />
                  <span>{{ getAlreadyDoneLabel(doc) }}</span>
                </div>

                <div class="footer-btn" style="position: relative;" @click="toggleRejectMenu(idx)">
                  <van-icon name="cross" color="#999" />
                  <span>{{ $t('not_suitable') }}</span>

                  <div v-show="showRejectMenuIndex === idx" class="reject-popover" @click.stop>
                    <div
                      v-for="reason in rejectReasons"
                      :key="reason"
                      class="reject-item"
                      @click="submitRejectReason(reason, idx)"
                    >
                      {{ reason }}
                    </div>
                    <div class="popover-arrow"></div>
                  </div>
                </div>
                <div v-if="showRejectMenuIndex === idx" class="fixed-overlay" @click="toggleRejectMenu(idx)"></div>
              </div>

              <div v-if="isGeneratingPreviewSuggestion(doc)" class="generating-preview-overlay">
                <div class="generating-preview-pill">
                  <span class="generating-preview-spinner"></span>
                  <span>{{ $t('nba_suggestions_generating_preview') }}</span>
                </div>
              </div>
            </div>
          </transition-group>

          <button
            v-if="displaySuggestionList.length > 1 && activeSuggestionIndex < displaySuggestionList.length - 1"
            type="button"
            class="strategy-nav-btn strategy-nav-next"
            aria-label="Next suggestion"
            @click.stop="goToAdjacentSuggestion(1)"
          >
            <span class="strategy-nav-icon">
              <van-icon name="arrow" size="18" />
            </span>
          </button>
        </div>
      </div>
    </div>

    <transition name="slide-right">
      <swipeDialog
        v-if="htmlContent"
        :backtxt="$t('btn_prev')"
        :title="$t('task_content_preview')"
        @closeFn="closePreviewFn"
      >
        <div v-if="htmlContent" class="html-preview" v-html="htmlContent"></div>
      </swipeDialog>
    </transition>
  </div>
</template>

<script>
import { Icon, Dialog } from 'vant';
import { faCircleCheck, faClone } from '@fortawesome/free-regular-svg-icons';
import {
  getNbaSuggestions,
  saveNbaSuggestions,
  saveNbaSuggestionFeedback
} from '@/api/Physicians';
import { streamNextBestActionSuggestion } from '@/api/ai';
import { getContentById } from '@/api/content';
import { getLocalStorage } from '@/utils/index';
import { displayProductName } from '@/utils/productDisplay';
import swipeDialog from '@/views/layout/swipeDialog';
import {
  getCanonicalNbaActionType,
  getNbaActionTypeClass,
  getNbaActionTypeI18nKey,
  getNbaActionTypeIcon,
  getNbaTriggerTypeI18nKey,
  normalizeNbaEnumValue
} from '@/utils/nbaSuggestionEnums';

const activeNbaSuggestionJobs = Object.create(null);
const NBA_SAVE_RELOAD_RETRY_DELAYS = [1200, 2000, 3000];

export default {
  name: 'NbaSuggestions',
  components: {
    [Icon.name]: Icon,
    swipeDialog
  },
  props: {
    physicianId: {
      type: [String, Number],
      default: ''
    }
  },
  data() {
    return {
      loading: true,
      defaultAvatar: require('@/assets/lundbeck/avatar-unknown.png'),
      acceptSuggestionIcon: faCircleCheck,
      alreadyDoneIcon: faClone,
      suggestionList: [],
      refreshing: false,
      autoGeneratingOnEmpty: false,
      emptyAutoGenerationAttempted: false,
      activeNbaSuggestionJobRunning: false,
      generatingPreviewList: [],
      activeSuggestionIndex: 0,
      strategyCarouselScrollFrame: null,
      showRejectMenuIndex: -1,
      rejectReasons: [],
      relativeTimeNow: Date.now(),
      relativeTimeTimer: null,
      htmlContent: null,
      contentDetailCache: {}
    };
  },
  computed: {
    isSuggestionLoading() {
      return this.loading || this.autoGeneratingOnEmpty || this.activeNbaSuggestionJobRunning;
    },
    displaySuggestionList() {
      return this.suggestionList.length > 0 ? this.suggestionList : this.generatingPreviewList;
    },
    displayGeneratedTimeText() {
      const suggestion = this.displaySuggestionList.find(item => item && item.generated_time);
      return this.formatRelativeGeneratedTime(suggestion && suggestion.generated_time);
    }
  },
  watch: {
    '$i18n.locale'() {
      this.initRejectReasons();
    },
    suggestionList() {
      this.$nextTick(() => {
        this.updateActiveSuggestionIndex();
      });
      this.triggerEmptyAutoGeneration();
    }
  },
  mounted() {
    this._nbaSuggestionsActive = true;
    this.syncActiveNbaSuggestionJob();
    this.init();
    this.initRejectReasons();
    this.startRelativeTimeTimer();
  },
  beforeDestroy() {
    this._nbaSuggestionsActive = false;
    this.stopRelativeTimeTimer();
    if (this.strategyCarouselScrollFrame) {
      cancelAnimationFrame(this.strategyCarouselScrollFrame);
      this.strategyCarouselScrollFrame = null;
    }
  },
  methods: {
    isNbaSuggestionsActive() {
      return this._nbaSuggestionsActive !== false;
    },
    isEnglishLocale() {
      return /^en/i.test(this.$i18n.locale);
    },
    initRejectReasons() {
      this.rejectReasons = [
        this.$t('reject_reason_recent_visit'),
        this.$t('reject_reason_inconvenient'),
        this.$t('reject_reason_irrelevant'),
        this.$t('reject_reason_no_demand'),
        this.$t('reject_reason_other')
      ];
    },
    async init() {
      this.loading = true;
      try {
        await this.loadRecommendDoctor();
      } finally {
        if (this.isNbaSuggestionsActive()) {
          this.loading = false;
        }
      }
    },
    async refreshSuggestions() {
      if (this.refreshing || this.activeNbaSuggestionJobRunning) return;

      this.refreshing = true;
      this.activeNbaSuggestionJobRunning = true;
      this.showRejectMenuIndex = -1;

      try {
        await this.loadRecommendDoctor({
          forceRefresh: true,
          preserveOnError: true
        });

        if (this.isNbaSuggestionsActive()) {
          this.$nextTick(() => {
            this.resetStrategyCarousel();
            this.scrollToStrategyTop();
          });
        }
      } finally {
        if (this.isNbaSuggestionsActive()) {
          this.refreshing = false;
          this.syncActiveNbaSuggestionJob();
        }
      }
    },
    async confirmRefreshSuggestions() {
      if (this.loading || this.refreshing || this.activeNbaSuggestionJobRunning) return;

      try {
        await Dialog.confirm({
          title: this.$t('refresh_ai_confirm_title'),
          message: this.$t('refresh_ai_confirm_message'),
          confirmButtonText: this.$t('confirm'),
          cancelButtonText: this.$t('cancel'),
          showCancelButton: true
        });
        await this.refreshSuggestions();
      } catch (e) {
        // User cancelled.
      }
    },
    async loadRecommendDoctor(options = {}) {
      const {
        forceRefresh = false,
        preserveOnError = false
      } = options;

      try {
        let displayList = forceRefresh
          ? await this.generateAndSaveNbaSuggestions()
          : await this.loadCachedNbaSuggestions();

        if (!forceRefresh && !this.hasPendingNbaSuggestions(displayList)) {
          displayList = await this.generateSuggestionsForEmptyList();
        }

        this.applySuggestionList(displayList);
        return true;
      } catch (e) {
        console.error('Fetch Recommend Doctor failed', e);
        if (!preserveOnError && this.isNbaSuggestionsActive()) {
          this.generatingPreviewList = [];
          this.suggestionList = [];
        }
        return false;
      }
    },
    async triggerEmptyAutoGeneration() {
      if (
        this.loading ||
        this.refreshing ||
        this.autoGeneratingOnEmpty ||
        this.activeNbaSuggestionJobRunning ||
        this.emptyAutoGenerationAttempted ||
        this.suggestionList.length > 0
      ) {
        return;
      }

      try {
        const displayList = await this.generateSuggestionsForEmptyList();
        this.applySuggestionList(displayList);
      } catch (e) {
        console.error('Auto generate NBA suggestions failed', e);
      }
    },
    async generateSuggestionsForEmptyList() {
      this.emptyAutoGenerationAttempted = true;
      this.autoGeneratingOnEmpty = true;
      this.showRejectMenuIndex = -1;

      try {
        const displayList = await this.generateAndSaveNbaSuggestions();
        if (this.hasPendingNbaSuggestions(displayList)) {
          this.emptyAutoGenerationAttempted = false;
        }
        return displayList;
      } finally {
        if (this.isNbaSuggestionsActive()) {
          this.autoGeneratingOnEmpty = false;
          this.syncActiveNbaSuggestionJob();
        }
      }
    },
    async loadCachedNbaSuggestions() {
      const res = await getNbaSuggestions({});
      if (res && res.isSuccess === false) {
        throw new Error(res.message || 'GetNbaSuggestions failed');
      }
      return this.getApiDataList(res);
    },
    applySuggestionList(displayList) {
      const suggestionList = this.sortSuggestions(
        this.getPendingNbaSuggestions(displayList)
          .map(item => this.mapNbaSuggestionItem(item))
      );
      if (this.isNbaSuggestionsActive()) {
        this.generatingPreviewList = [];
        this.suggestionList = suggestionList;
      }
    },
    getPendingNbaSuggestions(list) {
      return (Array.isArray(list) ? list : []).filter(this.isPendingSuggestion);
    },
    hasPendingNbaSuggestions(list) {
      return this.getPendingNbaSuggestions(list).length > 0;
    },
    getNbaSuggestionBatchIds(list) {
      return new Set(
        (Array.isArray(list) ? list : [])
          .map(item => item && item.batch_id)
          .filter(Boolean)
          .map(batchId => String(batchId))
      );
    },
    getFreshPendingNbaSuggestions(list, excludedBatchIds = new Set()) {
      return this.getPendingNbaSuggestions(list).filter(item => {
        if (!item || !item.batch_id) return true;
        return !excludedBatchIds.has(String(item.batch_id));
      });
    },
    hasFreshPendingNbaSuggestions(list, excludedBatchIds = new Set()) {
      return this.getFreshPendingNbaSuggestions(list, excludedBatchIds).length > 0;
    },
    getNbaSuggestionJobParams(userContext = this.getUserContext()) {
      const content = this.buildAiContent(userContext);
      const lang = this.isEnglishLocale() ? 'en' : 'zh';
      return {
        content,
        lang,
        jobKey: `${content}&lang=${lang}`
      };
    },
    getCurrentNbaSuggestionJob() {
      return activeNbaSuggestionJobs[this.getNbaSuggestionJobParams().jobKey];
    },
    syncActiveNbaSuggestionJob() {
      const job = this.getCurrentNbaSuggestionJob();
      this.activeNbaSuggestionJobRunning = Boolean(job);

      if (!job || this._observedNbaSuggestionJob === job) return;
      this._observedNbaSuggestionJob = job;

      job
        .then(async (generatedList) => {
          if (!this.isNbaSuggestionsActive()) return;
          if (this.hasPendingNbaSuggestions(generatedList)) return;

          try {
            const apiList = await this.loadCachedNbaSuggestions();
            if (this.hasPendingNbaSuggestions(apiList)) {
              this.applySuggestionList(apiList);
            }
          } catch (e) {
            console.error('Reload saved NBA suggestions failed', e);
          }
        })
        .catch(e => {
          console.error('NBA suggestion generation failed', e);
        })
        .finally(() => {
          if (!this.isNbaSuggestionsActive() || this._observedNbaSuggestionJob !== job) return;
          this.activeNbaSuggestionJobRunning = false;
          this._observedNbaSuggestionJob = null;
        });
    },
    async generateAndSaveNbaSuggestions() {
      const { content, lang, jobKey } = this.getNbaSuggestionJobParams();

      if (!activeNbaSuggestionJobs[jobKey]) {
        activeNbaSuggestionJobs[jobKey] = this.runNbaSuggestionGeneration({
          content,
          lang
        }).finally(() => {
          delete activeNbaSuggestionJobs[jobKey];
        });
      }

      this.syncActiveNbaSuggestionJob();
      return activeNbaSuggestionJobs[jobKey];
    },
    async runNbaSuggestionGeneration({ content, lang }) {
      let aiRes = '';
      const streamState = {
        buffer: '',
        text: ''
      };
      const existingBatchIds = this.getNbaSuggestionBatchIds(this.suggestionList);

      await streamNextBestActionSuggestion({
        data: {
          content,
          lang
        },
        onChunk: (chunk) => {
          aiRes += chunk;
          if (this.processNbaSuggestionStreamChunk(chunk, streamState)) {
            this.updateGeneratingPreviewList(streamState.text);
          }
        },
        onError: (error) => {
          throw error;
        }
      });
      const aiSuggestions = this.extractAiSuggestionArray(aiRes)
        .filter(item => item && typeof item === 'object');

      if (!aiSuggestions.length) {
        return [];
      }

      const saveRes = await saveNbaSuggestions({
        suggestions: aiSuggestions.map(item => this.mapAiSuggestionForSave(item))
      });
      if (saveRes && saveRes.isSuccess === false) {
        throw new Error(saveRes.message || 'SaveNbaSuggestions failed');
      }

      const savedFromResponse = this.getApiDataList(saveRes);
      if (this.hasFreshPendingNbaSuggestions(savedFromResponse, existingBatchIds)) {
        return savedFromResponse;
      }

      try {
        const savedSuggestions = await this.loadFreshSavedNbaSuggestions(existingBatchIds);
        return this.hasFreshPendingNbaSuggestions(savedSuggestions, existingBatchIds) ? savedSuggestions : aiSuggestions;
      } catch (e) {
        console.error('Reload saved NBA suggestions failed', e);
        return aiSuggestions;
      }
    },
    async loadFreshSavedNbaSuggestions(excludedBatchIds = new Set()) {
      for (const delay of NBA_SAVE_RELOAD_RETRY_DELAYS) {
        await this.waitForNbaSuggestionReload(delay);
        if (!this.isNbaSuggestionsActive()) return [];

        const savedSuggestions = await this.loadCachedNbaSuggestions();
        if (this.hasFreshPendingNbaSuggestions(savedSuggestions, excludedBatchIds)) {
          return savedSuggestions;
        }
      }

      return [];
    },
    waitForNbaSuggestionReload(delay) {
      return new Promise(resolve => {
        setTimeout(resolve, delay);
      });
    },
    processNbaSuggestionStreamChunk(chunk, streamState) {
      if (!chunk || !streamState) return false;

      streamState.buffer += chunk;
      const lines = streamState.buffer.split('\n');
      streamState.buffer = lines.pop() || '';

      let changed = false;
      lines.forEach(line => {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data:')) return;

        const jsonText = trimmed.substring(5).trim();
        if (!jsonText || jsonText === '[DONE]') return;

        try {
          const event = JSON.parse(jsonText);
          const outputText = event && event.output && event.output.text;
          if (typeof outputText !== 'string' || !outputText) return;

          if (streamState.text && outputText.startsWith(streamState.text)) {
            streamState.text = outputText;
          } else {
            streamState.text += outputText;
          }
          changed = true;
        } catch (e) {
          // Ignore incomplete/non-JSON SSE lines; the final parser handles the full buffer.
        }
      });

      return changed;
    },
    updateGeneratingPreviewList(streamText) {
      if (!this.isNbaSuggestionsActive() || this.suggestionList.length > 0) return;

      const previewSuggestions = this.extractCompleteAiSuggestionObjects(streamText)
        .filter(item => item && typeof item === 'object')
        .slice(0, 6)
        .map((item, index) => this.mapNbaSuggestionItem({
          ...item,
          nba_suggestion_id: `generating-preview-${index}`,
          physician_name: item.physician_name || item.name || '',
          department_name: item.department_name || item.department || '',
          professional_title: item.professional_title || item.title || '',
          institution_name: item.institution_name || item.hospital || '',
          suggestion_type: item.action_type || item.suggestion_type || 'call',
          suggestion_reason_short: item.suggestion_reason_short || item.suggestion_reason || item.recommended_action || '',
          suggestion_reason: item.suggestion_detail || item.suggestion_reason || item.next_step || '',
          suggestion_content: JSON.stringify(item),
          _generatingPreview: true
        }));

      if (previewSuggestions.length) {
        this.generatingPreviewList = this.sortSuggestions(previewSuggestions);
      }
    },
    extractCompleteAiSuggestionObjects(streamText) {
      const text = this.normalizeAiJsonText(streamText);
      const arrayStart = text.indexOf('[');
      if (arrayStart < 0) return [];

      const objects = [];
      let inString = false;
      let escaped = false;
      let objectDepth = 0;
      let objectStart = -1;

      for (let i = arrayStart + 1; i < text.length; i++) {
        const char = text[i];

        if (escaped) {
          escaped = false;
          continue;
        }

        if (char === '\\' && inString) {
          escaped = true;
          continue;
        }

        if (char === '"') {
          inString = !inString;
          continue;
        }

        if (inString) continue;

        if (char === '{') {
          if (objectDepth === 0) {
            objectStart = i;
          }
          objectDepth++;
        } else if (char === '}' && objectDepth > 0) {
          objectDepth--;
          if (objectDepth === 0 && objectStart >= 0) {
            const objectText = text.slice(objectStart, i + 1);
            try {
              objects.push(JSON.parse(objectText));
            } catch (e) {
              // Skip malformed partial objects and keep parsing later completed ones.
            }
            objectStart = -1;
          }
        }
      }

      return objects;
    },
    normalizeAiJsonText(value) {
      return String(value || '')
        .replace(/^```(?:json)?/i, '')
        .replace(/```$/i, '')
        .trim();
    },
    getApiDataList(res) {
      if (!res) return [];
      if (Array.isArray(res)) return res;
      if (Array.isArray(res.data)) return res.data;
      if (Array.isArray(res.value)) return res.value;
      return [];
    },
    getAiLanguage() {
      return this.isEnglishLocale() ? 'en-US' : 'zh-CN';
    },
    getTodayString() {
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    getUserContext() {
      const userInfo = JSON.parse(getLocalStorage('userInfo') || '{}');
      return {
        user_id: getLocalStorage('user_id') || userInfo.userId || userInfo.id || '',
        user_name: getLocalStorage('user_name') || userInfo.userName || userInfo.name || '',
        territory_id: getLocalStorage('territory_id') || userInfo.territoryId || '',
        territory_type: getLocalStorage('territory_type') || userInfo.territoryType || '',
        role_id: getLocalStorage('role_id') || userInfo.roleId || ''
      };
    },
    buildAiContent(userContext) {
      const params = new URLSearchParams();
      params.set('user_id', userContext.user_id || '');
      params.set('role_id', userContext.role_id || '');
      params.set('territory_id', userContext.territory_id || '');
      params.set('today', this.getTodayString());
      params.set('language', this.getAiLanguage());
      return params.toString();
    },
    mapAiSuggestionForSave(item) {
      const sanitizedItem = this.sanitizeNbaSuggestionPayload(item);
      return {
        physician_id: sanitizedItem.physician_id || '',
        physician_name: sanitizedItem.physician_name || '',
        department_name: sanitizedItem.department_name || '',
        institution_id: sanitizedItem.institution_id || '',
        institution_name: sanitizedItem.institution_name || '',
        professional_title: sanitizedItem.professional_title || '',
        suggestion_type: getCanonicalNbaActionType(sanitizedItem.action_type || sanitizedItem.suggestion_type || 'call'),
        suggestion_reason_short: sanitizedItem.suggestion_reason_short || sanitizedItem.suggestion_reason || sanitizedItem.recommended_action || '',
        suggestion_reason: sanitizedItem.suggestion_detail || sanitizedItem.suggestion_reason || sanitizedItem.next_step || '',
        suggestion_content: JSON.stringify(sanitizedItem)
      };
    },
    extractAiSuggestionArray(res) {
      const candidates = [
        res,
        res && res.data,
        res && res.value,
        res && res.result,
        res && res.output,
        res && res.output && res.output.text,
        res && res.data && res.data.output,
        res && res.data && res.data.output && res.data.output.text
      ];

      for (const candidate of candidates) {
        const parsed = this.tryParseAiSuggestion(candidate);
        if (Array.isArray(parsed)) return parsed;
        if (parsed && Array.isArray(parsed.data)) return parsed.data;
        if (parsed && parsed.output) {
          const nested = this.tryParseAiSuggestion(parsed.output.text || parsed.output);
          if (Array.isArray(nested)) return nested;
        }
      }

      return [];
    },
    tryParseAiSuggestion(value) {
      if (Array.isArray(value)) return value;
      if (!value) return null;
      if (typeof value === 'object') return value;
      if (typeof value !== 'string') return null;

      const sseParsed = this.tryParseSseSuggestion(value);
      if (sseParsed) return sseParsed;

      const text = value
        .replace(/^```(?:json)?/i, '')
        .replace(/```$/i, '')
        .trim();
      const jsonText = text.startsWith('[')
        ? text
        : text.slice(text.indexOf('['), text.lastIndexOf(']') + 1);

      if (!jsonText) return null;

      try {
        return JSON.parse(jsonText);
      } catch (e) {
        return null;
      }
    },
    tryParseSseSuggestion(value) {
      if (!value || value.indexOf('data:') < 0) return null;

      const dataLines = value
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.startsWith('data:'))
        .map(line => line.substring(5).trim())
        .filter(line => line && line !== '[DONE]');

      let accumulatedText = '';

      for (const line of dataLines) {
        try {
          const event = JSON.parse(line);
          const outputText = event && event.output && event.output.text;
          if (typeof outputText !== 'string') continue;

          if (accumulatedText && outputText.startsWith(accumulatedText)) {
            accumulatedText = outputText;
          } else {
            accumulatedText += outputText;
          }
        } catch (e) {
          accumulatedText += line;
        }
      }

      return accumulatedText ? this.tryParseAiSuggestion(accumulatedText) : null;
    },
    parseSuggestionContent(content) {
      if (!content) return {};
      if (typeof content === 'object') return content;
      try {
        return JSON.parse(content);
      } catch (e) {
        return {};
      }
    },
    mapNbaSuggestionItem(item) {
      const detail = this.parseSuggestionContent(item.suggestion_content);
      const actionType = getCanonicalNbaActionType(item.suggestion_type || detail.action_type || '');
      const reason = item.suggestion_reason_short || detail.suggestion_reason || item.suggestion_reason || detail.suggestion_detail || '';

      const mapped = {
        ...item,
        ...detail,
        name: item.physician_name || detail.physician_name,
        department: item.department_name || detail.department_name,
        title: item.professional_title || detail.professional_title || '',
        hospital: item.institution_name || detail.institution_name,
        id: item.physician_id || detail.physician_id,
        physician_id: item.physician_id || detail.physician_id,
        physician_name: item.physician_name || detail.physician_name,
        suggestion_type: actionType,
        suggestion_reason: reason,
        suggestion_reason_detail: item.suggestion_reason || detail.suggestion_detail || reason,
        suggestion_content: item.suggestion_content || JSON.stringify(detail),
        recommended_topic: item.recommended_topic || detail.recommended_topic || '',
        recommended_content_id: item.recommended_content_id || detail.recommended_content_id || '',
        recommended_content_title: item.recommended_content_title || detail.recommended_content_title || '',
        recommended_action: item.recommended_action || detail.recommended_action || '',
        next_step: item.next_step || detail.next_step || '',
        recommended_campaign_id: item.recommended_campaign_id || detail.recommended_campaign_id || '',
        recommended_campaign_name: item.recommended_campaign_name || detail.recommended_campaign_name || '',
        days_since_last_call: item.days_since_last_call ?? detail.days_since_last_call,
        generated_time: item.generated_time || detail.generated_time || '',
        customer_level: this.getCustomerLevel(item, detail),
        is_key_customer: this.isKeyCustomer(item, detail),
        product_id: item.product_id || detail.product_id || '',
        product_name: item.product_name || detail.product_name || '',
        physician_segment: item.physician_segment || detail.physician_segment || '',
        product_sales_periods: item.product_sales_periods || detail.product_sales_periods || [],
        product_sales_signal: normalizeNbaEnumValue(item.product_sales_signal || detail.product_sales_signal),
        priority: normalizeNbaEnumValue(item.priority || detail.priority),
        trigger_type: normalizeNbaEnumValue(item.trigger_type || detail.trigger_type),
        feedback_status: item.feedback_status || detail.feedback_status || ''
      };
      return this.sanitizeNbaSuggestionPayload(mapped);
    },
    getCustomerLevel(item = {}, detail = {}) {
      return item.customer_level || detail.customer_level ||
        item.hcp_level || detail.hcp_level ||
        item.physician_level || detail.physician_level ||
        item.hcp_grade || detail.hcp_grade ||
        item.customer_grade || detail.customer_grade ||
        '';
    },
    isKeyCustomer(item = {}, detail = {}) {
      if (typeof item.is_key_customer === 'boolean') return item.is_key_customer;
      if (typeof detail.is_key_customer === 'boolean') return detail.is_key_customer;
      return ['A', 'B'].includes(String(this.getCustomerLevel(item, detail) || '').trim().toUpperCase());
    },
    hasProductContext(doc) {
      return Boolean(doc && (
        doc.product_name ||
        doc.physician_segment ||
        this.getProductSalesSignalLabel(doc.product_sales_signal)
      ));
    },
    getCustomerLevelLabel(doc) {
      const level = String(doc && doc.customer_level || '').trim().toUpperCase();
      if (!level) return this.$t('ai_nba_key_customer');
      return this.$t('ai_nba_customer_level', { level });
    },
    getProductSalesSignalLabel(signal) {
      const keyMap = {
        continuous_decline: 'ai_nba_product_sales_signal_continuous_decline',
        sales_drop: 'ai_nba_product_sales_signal_sales_drop',
        target_gap: 'ai_nba_product_sales_signal_target_gap',
        abnormal_fluctuation: 'ai_nba_product_sales_signal_abnormal_fluctuation'
      };
      const i18nKey = keyMap[normalizeNbaEnumValue(signal)];
      return i18nKey ? this.$t(i18nKey) : '';
    },
    sanitizeNbaSuggestionPayload(suggestion = {}) {
      const sanitized = { ...suggestion };
      if (!this.hasSensitiveSalesText(sanitized)) return sanitized;

      const safeText = this.getSafeProductTrendText(sanitized);
      [
        'suggestion_reason_short',
        'suggestion_reason',
        'suggestion_reason_detail',
        'suggestion_detail',
        'recommended_action',
        'next_step'
      ].forEach((field) => {
        if (sanitized[field] && this.containsSalesSensitiveTerms(sanitized[field])) {
          sanitized[field] = safeText;
        }
      });

      if (Array.isArray(sanitized.trigger_evidence)) {
        sanitized.trigger_evidence = sanitized.trigger_evidence.map(evidence =>
          this.containsSalesSensitiveTerms(evidence) ? safeText : evidence
        );
      }

      return sanitized;
    },
    hasSensitiveSalesText(suggestion = {}) {
      const triggerType = normalizeNbaEnumValue(suggestion.trigger_type);
      const sourceAgent = normalizeNbaEnumValue(suggestion.source_agent);
      const sourceRule = normalizeNbaEnumValue(suggestion.source_rule);
      const salesSignal = normalizeNbaEnumValue(suggestion.product_sales_signal);
      const isProductTrendSuggestion = triggerType === 'product_sales_trend' ||
        sourceAgent === 'nba_sales_trend_agent' ||
        sourceRule.indexOf('product_sales') >= 0 ||
        sourceRule.indexOf('product_target') >= 0 ||
        Boolean(salesSignal && salesSignal !== 'no_signal' && salesSignal !== 'null');

      const textFields = [
        suggestion.suggestion_reason_short,
        suggestion.suggestion_reason,
        suggestion.suggestion_reason_detail,
        suggestion.suggestion_detail,
        suggestion.recommended_action,
        suggestion.next_step,
        ...(Array.isArray(suggestion.trigger_evidence) ? suggestion.trigger_evidence : [])
      ];

      return isProductTrendSuggestion && textFields.some(text => this.containsSalesSensitiveTerms(text));
    },
    containsSalesSensitiveTerms(value) {
      const text = String(value || '');
      return /(销量|销售额|销售|目标|达成|低于|处方|推广|冲量|%|％|\d+\s*月|\d+(?:\.\d+)?\s*(?:%|％)|sales|target|achievement|prescription)/i.test(text);
    },
    getSafeProductTrendText(suggestion = {}) {
      const institution = suggestion.institution_name || suggestion.hospital || '';
      const product = displayProductName(suggestion.product_name || '', suggestion.product_id || '');
      if (this.isEnglishLocale()) {
        return `${institution ? `${institution} ` : ''}${product ? `${product} ` : ''}recent in-hospital product trend signals suggest follow-up on access, inventory, and compliant scientific communication opportunities.`;
      }
      return `${institution}${product}近期院内产品趋势出现波动，建议了解院内准入、库存及合规学术沟通机会。`;
    },
    startRelativeTimeTimer() {
      this.stopRelativeTimeTimer();
      this.relativeTimeTimer = setInterval(() => {
        this.relativeTimeNow = Date.now();
      }, 60000);
    },
    stopRelativeTimeTimer() {
      if (!this.relativeTimeTimer) return;
      clearInterval(this.relativeTimeTimer);
      this.relativeTimeTimer = null;
    },
    formatRelativeGeneratedTime(value) {
      const date = this.parseGeneratedDate(value);
      if (!date) return '';

      const diffMs = Math.max(0, this.relativeTimeNow - date.getTime());
      const minuteMs = 60 * 1000;
      const hourMs = 60 * minuteMs;
      const dayMs = 24 * hourMs;
      const monthMs = 30 * dayMs;

      if (diffMs < minuteMs) return this.$t('nba_generated_just_now');
      if (diffMs < hourMs) {
        const count = Math.floor(diffMs / minuteMs);
        return this.$t(count === 1 ? 'nba_generated_minute_ago' : 'nba_generated_minutes_ago', { count });
      }
      if (diffMs < dayMs) {
        const count = Math.floor(diffMs / hourMs);
        return this.$t(count === 1 ? 'nba_generated_hour_ago' : 'nba_generated_hours_ago', { count });
      }
      if (diffMs < monthMs) {
        const count = Math.floor(diffMs / dayMs);
        return this.$t(count === 1 ? 'nba_generated_day_ago' : 'nba_generated_days_ago', { count });
      }

      return this.$t('nba_generated_date', { date: this.formatGeneratedDate(value, date) });
    },
    parseGeneratedDate(value) {
      if (!value) return null;
      const date = new Date(String(value).trim());
      return Number.isNaN(date.getTime()) ? null : date;
    },
    formatGeneratedDate(value, parsedDate = null) {
      const text = String(value || '').trim();
      const normalizedMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (normalizedMatch) {
        return `${normalizedMatch[1]}-${normalizedMatch[2]}-${normalizedMatch[3]}`;
      }

      const date = parsedDate || this.parseGeneratedDate(value);
      if (!date) return text;

      const pad = num => String(num).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    },
    sortSuggestions(list) {
      const priorityRank = {
        high: 0,
        medium: 1,
        low: 2
      };
      return list.sort((a, b) => {
        const priorityDiff = (priorityRank[a.priority] ?? 99) - (priorityRank[b.priority] ?? 99);
        if (priorityDiff !== 0) return priorityDiff;
        return (Number(b.recommendation_score) || 0) - (Number(a.recommendation_score) || 0);
      });
    },
    isPendingSuggestion(item) {
      return !item.feedback_status;
    },
    scrollToStrategyTop() {
      const strategyRoot = this.$refs.strategyRoot;
      if (!strategyRoot || typeof strategyRoot.scrollIntoView !== 'function') return;

      strategyRoot.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    },
    resetStrategyCarousel() {
      const strategyCarousel = this.getStrategyCarouselEl();
      if (!strategyCarousel) return;

      strategyCarousel.scrollLeft = 0;
      this.activeSuggestionIndex = 0;
    },
    getStrategyCarouselEl() {
      const strategyCarousel = this.$refs.strategyCarousel;
      return strategyCarousel && strategyCarousel.$el ? strategyCarousel.$el : strategyCarousel;
    },
    getStrategyCardEls() {
      const strategyCarousel = this.getStrategyCarouselEl();
      if (!strategyCarousel) return [];
      return Array.from(strategyCarousel.querySelectorAll('.strategy-card'));
    },
    getActiveSuggestionIndex() {
      const strategyCarousel = this.getStrategyCarouselEl();
      const cards = this.getStrategyCardEls();
      if (!strategyCarousel || !cards.length) return 0;

      const carouselCenter = strategyCarousel.scrollLeft + (strategyCarousel.clientWidth / 2);
      let activeIndex = 0;
      let minDistance = Infinity;

      cards.forEach((card, index) => {
        const cardCenter = card.offsetLeft + (card.offsetWidth / 2);
        const distance = Math.abs(cardCenter - carouselCenter);
        if (distance < minDistance) {
          minDistance = distance;
          activeIndex = index;
        }
      });

      return activeIndex;
    },
    updateActiveSuggestionIndex() {
      const cards = this.getStrategyCardEls();
      if (!cards.length) {
        this.activeSuggestionIndex = 0;
        return;
      }

      this.activeSuggestionIndex = this.getActiveSuggestionIndex();
    },
    handleStrategyCarouselScroll() {
      if (this.strategyCarouselScrollFrame) {
        cancelAnimationFrame(this.strategyCarouselScrollFrame);
      }

      this.strategyCarouselScrollFrame = requestAnimationFrame(() => {
        this.updateActiveSuggestionIndex();
        this.strategyCarouselScrollFrame = null;
      });
    },
    scrollToSuggestionIndex(index) {
      const strategyCarousel = this.getStrategyCarouselEl();
      const cards = this.getStrategyCardEls();
      if (!strategyCarousel || !cards.length) return;

      const targetIndex = Math.max(0, Math.min(index, cards.length - 1));
      const targetCard = cards[targetIndex];
      const targetLeft = targetCard.offsetLeft - ((strategyCarousel.clientWidth - targetCard.offsetWidth) / 2);

      this.activeSuggestionIndex = targetIndex;
      strategyCarousel.scrollTo({
        left: targetLeft,
        behavior: 'smooth'
      });
    },
    goToAdjacentSuggestion(direction) {
      if (this.displaySuggestionList.length <= 1) return;
      this.showRejectMenuIndex = -1;
      this.scrollToSuggestionIndex(this.getActiveSuggestionIndex() + direction);
    },
    isGeneratingPreviewSuggestion(doc) {
      return Boolean(doc && doc._generatingPreview);
    },
    handleSuggestionDoctorClick(doc) {
      if (this.isGeneratingPreviewSuggestion(doc)) return;
      this.$emit('go-doctor-detail', doc.id);
    },
    toggleRejectMenu(idx) {
      this.showRejectMenuIndex = this.showRejectMenuIndex === idx ? -1 : idx;
    },
    getSuggestionKey(doc, idx) {
      if (!doc) return `nba-suggestion-${idx}`;
      return doc.nba_suggestion_id || doc.action_id || `${doc.physician_id || 'unknown'}-${doc.product_id || 'no-product'}-${doc.suggestion_type || idx}`;
    },
    isContentPushSuggestion(doc) {
      return getCanonicalNbaActionType(doc && doc.suggestion_type) === 'content_push';
    },
    getContentPushTopic(doc) {
      return doc && (doc.recommended_topic || doc.topic || '');
    },
    getContentPushContentId(doc) {
      return doc && (doc.recommended_content_id || doc.content_id || '');
    },
    getContentPushContentTitle(doc) {
      return doc && (doc.recommended_content_title || doc.content_title || '');
    },
    getContentPushPrimaryText(doc) {
      return this.getContentPushContentTitle(doc) || this.getContentPushTopic(doc) || this.$t('select_approved_content');
    },
    getContentPushDetailText(doc) {
      if (this.getContentPushContentTitle(doc)) {
        return this.getContentPushTopic(doc);
      }
      return doc && (doc.next_step || doc.recommended_action || '');
    },
    getAiHelperLabel(doc) {
      return this.isContentPushSuggestion(doc) ? this.$t('ask_ai_prepare_content') : this.$t('ask_ai_prepare_action');
    },
    getAiHelperPayload(doc) {
      if (!this.isContentPushSuggestion(doc)) {
        return doc;
      }
      return {
        ...doc,
        recommended_content_id: this.getContentPushContentId(doc),
        recommended_content_title: this.getContentPushContentTitle(doc),
        original_content_title: this.getContentPushContentTitle(doc),
      };
    },
    getPrimaryActionLabel(doc) {
      return this.isContentPushSuggestion(doc) ? this.$t('send_content') : this.$t('create_visit_plan');
    },
    getPrimaryActionIcon(doc) {
      return this.isContentPushSuggestion(doc) ? ['fas', 'paper-plane'] : this.acceptSuggestionIcon;
    },
    getPrimaryActionClass(doc) {
      return {
        'is-content-push': this.isContentPushSuggestion(doc)
      };
    },
    getAlreadyDoneLabel(doc) {
      return this.isContentPushSuggestion(doc) ? this.$t('content_sent') : this.$t('visited');
    },
    async handlePrimarySuggestionAction(doc) {
      if (this.isContentPushSuggestion(doc)) {
        await this.handleSendContentSuggestion(doc);
        return;
      }
      await this.handleAcceptSuggestion(doc);
    },
    async handleAcceptSuggestion(doc) {
      this.showRejectMenuIndex = -1;
      if (!doc || !doc.physician_id) return;
      await this.submitNbaFeedback(doc, 'accepted', {
        feedback_comment: 'create_visit_plan'
      });
      this.$emit('go-create-visit', doc);
    },
    async handleSendContentSuggestion(doc) {
      this.showRejectMenuIndex = -1;
      if (!doc || !doc.physician_id) return;

      const contentId = this.getContentPushContentId(doc);
      if (!contentId) {
        this.$emit('go-send-content', {
          ...doc,
          recommended_topic: this.getContentPushTopic(doc),
          recommended_content_id: '',
          recommended_content_title: this.getContentPushContentTitle(doc)
        });
        return;
      }

      const content = await this.loadContentDetail(contentId);
      if (!content) {
        this.$toast.fail(this.$t('get_content_failed'));
        return;
      }

      await this.forwardContentSuggestion(doc);
    },
    async loadContentDetail(contentId) {
      if (!contentId) return null;
      if (this.contentDetailCache[contentId]) {
        return this.contentDetailCache[contentId];
      }

      const toast = this.$createToast({
        time: 10000,
        mask: false,
      });
      toast.show();

      try {
        const res = await getContentById(contentId);
        const list = res && Array.isArray(res.value)
          ? res.value
          : (res && Array.isArray(res.data) ? res.data : []);
        const content = list[0] || null;
        if (content) {
          this.$set(this.contentDetailCache, contentId, content);
        }
        return content;
      } catch (e) {
        console.error('Failed to load NBA content detail:', e);
        return null;
      } finally {
        toast.hide();
      }
    },
    async previewContentSuggestion(doc) {
      const content = await this.loadContentDetail(this.getContentPushContentId(doc));
      if (!content) {
        this.$toast.fail(this.$t('get_content_failed'));
        return;
      }
      if (content.content_body) {
        this.htmlContent = content.content_body;
        this.$store.dispatch('setDialogStatus', true);
        return;
      }
      this.$toast(this.$t('no_preview_content'));
    },
    closePreviewFn() {
      this.$store.dispatch('setDialogStatus', false);
      this.htmlContent = null;
    },
    async forwardContentSuggestion(doc) {
      const toast = this.$createToast({
        time: 10000,
        mask: false,
      });

      try {
        toast.show();
        const saved = await this.submitNbaFeedback(doc, 'accepted', {
          feedback_comment: 'send_content'
        });
        if (saved) {
          this.removeSuggestion(doc);
        }
        this.showDemoContentPushSuccess();
      } catch (err) {
        console.error('Forward NBA content error:', err);
        this.$toast.fail(this.$t('forward_failed'));
      } finally {
        toast.hide();
      }
    },
    showDemoContentPushSuccess() {
      const message = this.$t('demo_content_pushed_to_customer_wechat');
      if (this.$toast && this.$toast.success) {
        this.$toast.success(message);
        return;
      }
      this.$toast(message);
    },
    async markAsVisited(idx) {
      const doc = this.suggestionList[idx];
      if (!doc) return;

      const saved = await this.submitNbaFeedback(doc, 'accepted', {
        feedback_comment: this.isContentPushSuggestion(doc) ? 'content_already_sent' : 'already_done'
      });
      if (saved) {
        this.removeSuggestion(doc);
      }
    },
    removeSuggestion(doc) {
      if (!doc || !doc.physician_id) return;

      const targetKey = this.getSuggestionKey(doc);
      this.suggestionList = this.suggestionList.filter(item => this.getSuggestionKey(item) !== targetKey);
    },
    async submitRejectReason(reason, idx) {
      this.showRejectMenuIndex = -1;
      const doc = this.suggestionList[idx];
      if (!doc) return;

      const saved = await this.submitNbaFeedback(doc, 'rejected', {
        reject_reason: reason,
        feedback_comment: reason
      });
      if (saved) {
        this.removeSuggestion(doc);
      }
    },
    async submitNbaFeedback(doc, feedbackStatus, extra = {}) {
      if (!doc || !doc.nba_suggestion_id) {
        console.warn('Skip NBA feedback: missing nba_suggestion_id', doc);
        return false;
      }

      const userContext = this.getUserContext();

      try {
        const res = await saveNbaSuggestionFeedback({
          nba_suggestion_id: doc.nba_suggestion_id,
          feedback_status: feedbackStatus,
          reject_reason: extra.reject_reason || '',
          feedback_comment: extra.feedback_comment || '',
          user_id: userContext.user_id,
          territory_id: userContext.territory_id,
          territory_type: userContext.territory_type,
          role_id: userContext.role_id
        });
        if (res && res.isSuccess === false) {
          throw new Error(res.message || 'NbaSuggestionFeedback failed');
        }
        return true;
      } catch (e) {
        console.error('Save NBA feedback failed', e);
        return false;
      }
    },
    getDaysText(days) {
      const parsedDays = Math.floor(Number(days));
      const elapsedDays = isNaN(parsedDays) ? 0 : Math.max(parsedDays, 0);
      const isEnglish = this.isEnglishLocale();

      if (elapsedDays === 0) {
        return isEnglish ? this.$t('today') : '今天';
      }

      if (!isEnglish) {
        if (elapsedDays === 1) return '昨天';
        if (elapsedDays === 2) return '前天';
      }

      if (elapsedDays > 30) {
        const months = Math.floor(elapsedDays / 30);
        return isEnglish
          ? `${months} ${months === 1 ? 'month' : 'months'} ago`
          : `${months}个月前`;
      }

      return isEnglish
        ? `${elapsedDays} ${elapsedDays === 1 ? 'day' : 'days'} ago`
        : `${elapsedDays}天前`;
    },
    getActionTypeLabel(type) {
      return this.$t(getNbaActionTypeI18nKey(type));
    },
    getActionTypeIcon(type) {
      return getNbaActionTypeIcon(type);
    },
    getActionTypeClass(type) {
      return getNbaActionTypeClass(type);
    },
    getTriggerTypeLabel(triggerType) {
      const i18nKey = getNbaTriggerTypeI18nKey(triggerType);
      return i18nKey ? this.$t(i18nKey) : '';
    }
  }
};
</script>

<style scoped lang="scss">
.section-header-simple {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
  color: #fff;
  font-weight: 500;
  margin-bottom: 12px;
  padding: 0 4px;
}

.section-title-group {
  display: flex;
  align-items: center;
  min-width: 0;
}

.section-header-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;
}

.suggestions-generated-time-summary {
  color: rgba(255, 255, 255, 0.72);
  font-size: 11px;
  font-weight: 400;
  line-height: 14px;
  letter-spacing: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
  padding: 2px 0;
  border: none;
  background: transparent;
  cursor: pointer;
  opacity: 0.72;
  transition: opacity 0.2s ease;
}

.refresh-btn:focus {
  outline: none;
}

.refresh-btn:hover {
  opacity: 0.9;
}

.refresh-btn:active {
  opacity: 1;
}

.refresh-btn:disabled {
  cursor: default;
}

.refresh-btn.is-refreshing {
  opacity: 0.9;
  pointer-events: none;
}

.refresh-btn-icon {
  color: rgba(255, 255, 255, 0.86);
}

.refresh-btn.is-refreshing .refresh-btn-icon {
  animation: refresh-spin 0.8s linear infinite;
}

.refresh-btn-text {
  font-size: 10px;
  font-weight: 400;
  line-height: 1;
  color: rgba(255, 255, 255, 0.86);
  letter-spacing: 0;
}

.section-refresh-btn {
  height: 22px;
  padding: 0 6px;
  border: 1px solid rgba(255, 255, 255, 0.58);
  border-radius: 11px;
  background: transparent;
  box-sizing: border-box;
}

.section-card {
  margin-bottom: 24px;
}

.strategy-carousel-shell {
  position: relative;
}

.strategy-carousel {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  scroll-padding-left: 10vw;
  scroll-padding-right: 10vw;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  padding: 0 0 4px;
  scrollbar-width: none;
}

.strategy-carousel::before,
.strategy-carousel::after {
  content: '';
  flex: 0 0 calc(10vw - 10px);
}

.strategy-carousel::-webkit-scrollbar {
  display: none;
}

.strategy-nav-btn {
  position: absolute;
  top: 0;
  bottom: 4px;
  z-index: 3;
  width: 42px;
  border: none;
  padding: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:focus {
    outline: none;
  }

  &:active .strategy-nav-icon {
    transform: scale(0.94);
  }
}

.strategy-nav-prev {
  left: 0;
}

.strategy-nav-next {
  right: 0;
}

.strategy-nav-icon {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  color: #00b852;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.16);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.16s ease;
  pointer-events: none;
}

.strategy-card {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  flex: 0 0 80vw;
  width: 80vw;
  scroll-snap-align: center;
  scroll-snap-stop: always;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  padding: 16px 16px 0 16px;

  &:first-of-type {
    margin-left: -10vw;
  }

  .system-recommendation-banner {
    background: linear-gradient(90deg, #f0fff6 0%, #fbfffd 100%);
    color: #8c4e4e;
    font-size: 12px;
    font-weight: 500;
    padding: 12px 16px 12px 16px;
    margin: -16px -16px 16px -16px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0 0 50% 50% / 0 0 15px 15px;
    position: relative;
    z-index: 0;

    .banner-text {
      letter-spacing: 0.5px;
      font-weight: 400;
    }

    .banner-count {
      position: absolute;
      right: 14px;
      top: 50%;
      transform: translateY(-50%);
      padding: 2px 8px;
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.78);
      color: #00b852;
      font-size: 11px;
      font-weight: 500;
      line-height: 16px;
      letter-spacing: 0;
    }
  }

  .card-top-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 12px;

    .doctor-info-simple {
      display: flex;
      align-items: center;
      flex: 1;
      min-width: 0;

      .avatar-wrapper {
        width: 48px;
        height: 48px;
        background: #f0f0f0;
        border-radius: 50%;
        margin-right: 10px;
        display: flex;
        justify-content: center;
        align-items: center;
        overflow: hidden;
        .avatar-img {
          width: 50px;
          height: 50px;
          object-fit: cover;
          opacity: 0.5;
        }
      }
      .info-text {
        min-width: 0;

        .name {
          font-size: 16px;
          font-weight: bold;
          color: #333;
          line-height: 1.3;
        }
        .details {
          font-size: 12px;
          color: #666;

          .dept {
            margin-top: 3px;
            line-height: 1.4;
          }

          .hospital {
            display: block;
            color: #999;
            font-size: 11px;
            line-height: 1.4;
            margin-top: 2px;
          }
        }
      }
    }

    .nba-meta-pills {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 6px;
      flex-shrink: 0;
      max-width: 104px;
    }

    .nba-action-pill {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      max-width: 100%;
      min-height: 20px;
      box-sizing: border-box;
      border-radius: 10px;
      padding: 2px 7px;
      font-size: 10px;
      line-height: 14px;
      font-weight: 500;
      letter-spacing: 0;
      white-space: normal;
      text-align: center;
    }

    .nba-action-pill {
      gap: 4px;
      background: #f1fff7;
      color: #00b852;

      &.action-content_push {
        background: #eef6ff;
        color: #1f6fb5;
      }

      &.action-meeting_invite {
        background: #f4f0ff;
        color: #6841b8;
      }

      &.action-meeting_speaker {
        background: #f0f8f4;
        color: #287a4d;
      }
    }

    .nba-pill-icon {
      flex-shrink: 0;
      font-size: 9px;
    }

    .nba-product-pill,
    .nba-customer-pill {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      max-width: 100%;
      min-height: 20px;
      box-sizing: border-box;
      border-radius: 10px;
      padding: 2px 7px;
      font-size: 10px;
      line-height: 14px;
      font-weight: 500;
      letter-spacing: 0;
      white-space: normal;
      text-align: center;
    }

    .nba-product-pill {
      background: #f2f7f4;
      color: #2f6f4e;
    }

    .nba-customer-pill {
      background: #f1fff7;
      color: #9a5f00;
    }

    .ai-helper-btn {
      background: linear-gradient(#fff, #fff) padding-box, linear-gradient(165deg, #006b4f 0%, #008f72 50%, #00b852 100%) border-box;
      border: 1.5px solid transparent;
      padding: 6px 12px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 500;
      display: inline-flex;
      align-self: center;
      flex-shrink: 0;

      .ai-gradient-text {
        display: flex;
        align-items: center;
        background: linear-gradient(165deg, #006b4f 0%, #008f72 50%, #00b852 100%);
        background-clip: text;
        -webkit-background-clip: text;
        color: transparent;
        white-space: nowrap;
      }
    }
  }

  .info-content-box {
    background: transparent;
    border-top: 1px solid #ededed;
    border-radius: 0;
    padding: 12px 0 0;
    margin-bottom: 16px;

    .product-context-row {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 6px;
      margin: 0 0 10px;
      min-width: 0;
    }

    .product-context-label {
      color: #999;
      font-size: 11px;
      line-height: 16px;
    }

    .product-context-value {
      color: #333;
      font-size: 12px;
      font-weight: 600;
      line-height: 16px;
    }

    .product-context-tag {
      display: inline-flex;
      align-items: center;
      max-width: 100%;
      min-height: 18px;
      padding: 1px 6px;
      border-radius: 9px;
      background: #f5f5f5;
      color: #666;
      font-size: 10px;
      line-height: 14px;
      box-sizing: border-box;

      &.is-signal {
        background: #f1fff7;
        color: #00b852;
      }
    }

    .info-row-flex {
      display: grid;
      grid-template-columns: minmax(86px, 0.62fr) minmax(0, 1.38fr);
      gap: 0;

      .info-panel {
        background: transparent;
        border: none;
        border-radius: 0;
        padding: 2px 0;
        min-width: 0;

        .info-panel-label {
          display: flex;
          align-items: center;
          font-size: 11px;
          color: #999;

          .van-icon {
            margin-right: 4px;
          }

          .reason-trigger-tag {
            display: inline-flex;
            align-items: center;
            margin-left: auto;
            max-width: 58%;
            padding: 1px 6px;
            border-radius: 10px;
            background: #fafafa;
            color: #00b852;
            font-size: 10px;
            line-height: 15px;
            font-weight: 600;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }

        .info-panel-value {
          margin-top: 8px;
          font-size: 16px;
          font-weight: bold;
          line-height: 1.4;
          color: #333;

          &.reason {
            font-size: 12px;
            font-weight: 400;
            line-height: 1.55;
          }

          &.no-visit-record {
            font-size: 12px;
            font-weight: 500;
            color: #666;
          }
        }

        .info-panel-desc {
          margin-top: 6px;
          font-size: 10px;
          line-height: 1.5;
          color: #00b852;
        }

        &.last-visit-panel {
          padding-right: 12px;

          .info-panel-value {
            font-size: 14px;
            font-weight: 500;
          }
        }

        &.reason-panel {
          border-left: 1px solid #ededed;
          padding-left: 14px;
        }
    }
  }
  }

  .content-push-box {
    margin: -4px 0 14px;
    padding: 10px 12px;
    border: 1px solid #dbeafe;
    border-radius: 10px;
    background: #f8fbff;
    overflow: hidden;
  }

  .content-push-label {
    display: flex;
    align-items: center;
    gap: 5px;
    color: #1f6fb5;
    font-size: 11px;
    font-weight: 600;
    line-height: 16px;
  }

  .content-preview-btn {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    margin-left: auto;
    padding: 0;
    border: none;
    background: transparent;
    color: #1f6fb5;
    font-size: 11px;
    line-height: 16px;
    font-weight: 500;
  }

  .content-push-title {
    margin-top: 6px;
    color: #2c3034;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.35;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .content-push-topic {
    margin-top: 5px;
    color: #64748b;
    font-size: 11px;
    line-height: 1.45;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

    .summary-section {
       border-top: 1px dashed #eee;
       padding-top: 12px;
       margin-top: 12px;

       .summary-title {
         font-size: 13px;
         font-weight: bold;
         color: #333;
         margin-bottom: 10px;
       }

       .summary-content {
         font-size: 12px;
         color: #666;
         line-height: 1.6;
       }

       .summary-text {
         text-align: justify;
         margin-bottom: 8px;
       }

       .summary-bullet {
         margin-bottom: 6px;
       }

       .summary-bullet-label {
         font-weight: bold;
         color: #333;
       }

       .feedback-group-card {
         border: 1px solid #f3f3f3;
         border-radius: 10px;
         background: #fff;
         padding: 10px 12px;
         margin-bottom: 8px;
       }

       .feedback-theme {
         font-weight: bold;
         color: #555;
         font-size: 12px;
         margin-bottom: 8px;
       }

       .feedback-item {
         margin-bottom: 8px;

         &:last-child {
           margin-bottom: 0;
         }
       }

       .feedback-question,
       .feedback-answer {
         display: flex;
         align-items: flex-start;
         line-height: 1.5;
       }

       .feedback-question {
         color: #666;
       }

       .feedback-answer {
         color: #333;
         margin-top: 4px;
       }

       .qa-tag {
         display: inline-flex;
         align-items: center;
         justify-content: center;
         width: 18px;
         height: 18px;
         border-radius: 50%;
         background: #e5f8ed;
         color: #00b852;
         font-size: 10px;
         font-weight: bold;
         margin-right: 6px;
         flex-shrink: 0;
       }

       .empty-feedback-text {
         color: #999;
         padding: 4px 0;
       }
    }

  .card-footer {
    border-top: 1px solid #ebedf0;
    margin: auto -16px 0;
    padding: 12px 0;
    display: flex;
    flex-shrink: 0;

    .footer-btn {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 12px;
      color: #999;
      cursor: pointer;

      .van-icon {
        margin-right: 6px;
      }

      .footer-btn-icon {
        margin-right: 6px;
      }

      &:first-child {
        border-right: 1px solid #ebedf0;
      }
    }
  }
}

.strategy-card-transition-move,
.strategy-card-transition-enter-active,
.strategy-card-transition-leave-active {
  transition:
    transform 0.35s ease,
    opacity 0.28s ease,
    flex-basis 0.35s ease,
    width 0.35s ease,
    padding 0.35s ease;
}

.strategy-card-transition-enter {
  opacity: 0;
  transform: translateX(18px) scale(0.98);
}

.strategy-card-transition-leave-to {
  flex-basis: 0 !important;
  width: 0 !important;
  opacity: 0;
  transform: translateX(-20px) scale(0.96);
  padding-right: 0 !important;
  padding-left: 0 !important;
}

.accept-action-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 14px;
}

.ai-helper-inline-btn {
  min-width: 0;
  padding: 0 12px;
  height: 28px;
  background: linear-gradient(#fff, #fff) padding-box, linear-gradient(165deg, #006b4f 0%, #008f72 50%, #00b852 100%) border-box;
  border: 1.5px solid transparent;
  border-radius: 16px;
  font-size: 11px;
}

.ai-helper-inline-btn .ai-gradient-text {
  display: flex;
  align-items: center;
  background: linear-gradient(165deg, #006b4f 0%, #008f72 50%, #00b852 100%);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  white-space: nowrap;
}

.accept-plan-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 96px;
  height: 28px;
  padding: 0 12px;
  border: 1.5px solid #00b852;
  border-radius: 16px;
  background: transparent;
  color: #00b852;
  font-size: 12px;
  font-weight: 600;
}

.accept-plan-btn.is-content-push {
  border-color: #1f6fb5;
  color: #1f6fb5;
}

.accept-plan-btn:focus {
  outline: none;
}

.accept-plan-btn:hover {
  background: rgba(0, 184, 82, 0.04);
}

.accept-plan-btn.is-content-push:hover {
  background: rgba(31, 111, 181, 0.05);
}

.accept-plan-btn:active {
  background: rgba(0, 184, 82, 0.08);
}

.accept-plan-btn.is-content-push:active {
  background: rgba(31, 111, 181, 0.1);
}

.accept-plan-btn-icon {
  font-size: 12px;
}

.strategy-card.is-generating-preview {
  position: relative;
  pointer-events: none;

  .card-top-row,
  .info-content-box,
  .content-push-box {
    opacity: 0.72;
  }
}

.generating-preview-overlay {
  position: absolute;
  inset: 0;
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  border-radius: inherit;
  background: rgba(255, 255, 255, 0.72);
}

.generating-preview-pill {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  max-width: 100%;
  padding: 0;
  background: transparent;
  border: none;
  box-shadow: none;
  color: #00b852;
  font-size: 12px;
  line-height: 16px;
}

.generating-preview-spinner {
  width: 12px;
  height: 12px;
  flex: 0 0 12px;
  border-radius: 50%;
  background: conic-gradient(from 0deg, rgba(0, 184, 82, 0.08), #00b852);
  -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 2px), #000 0);
  mask: radial-gradient(farthest-side, transparent calc(100% - 2px), #000 0);
  animation: refresh-spin 0.8s linear infinite;
}

.loading-wrapper {
  background: white;
  border: 1px solid #f0eeee;
  border-radius: 18px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  display: inline-flex;
  justify-content: center;
  align-items: center;
  min-height: 0;
  margin: 4px 0 10px 4px;
  padding: 12px 18px;
  position: relative;
  width: auto;

  .loading-dots {
    display: flex;
    align-items: center;
    gap: 6px;

    span {
      width: 7px;
      height: 7px;
      background-color: #7bbf99;
      border-radius: 50%;
      display: inline-block;
      animation: bounce 1.4s infinite ease-in-out both;
    }

    span:nth-child(1) {
      animation-delay: -0.32s;
    }

    span:nth-child(2) {
      animation-delay: -0.16s;
    }
  }
}

.nba-empty-state {
  color: #999;
  font-size: 13px;
  line-height: 20px;
  padding: 18px 4px 22px;
  text-align: center;
}

@keyframes bounce {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

@keyframes refresh-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.ai-loading-container {
  display: flex;
  align-items: center;
  padding: 6px 0;
}

.ai-bubble {
  background: #f2f3f5;
  padding: 8px 12px;
  border-radius: 0 12px 12px 12px;
  display: inline-flex;
  align-items: center;
  border: none;
  box-shadow: none;
}

.typing-indicator {
  display: inline-flex;
  align-items: center;
  height: 20px;
}

.dot {
  width: 5px;
  height: 5px;
  margin: 0 2px;
  background-color: #999;
  border-radius: 50%;
  animation: typing 1.4s infinite ease-in-out both;
}

.dot:nth-child(1) {
  animation-delay: -0.32s;
}

.dot:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes typing {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

.fixed-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99; // Above everything inside relative contexts
  background: transparent;
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.3s ease;
}

.slide-right-enter,
.slide-right-leave-to {
  transform: translateX(100%);
}

.html-preview {
  padding: 0;
  max-width: 100%;
  overflow: auto;

  ::v-deep img {
    width: 100%;
    height: auto;
    display: block;
  }
}

.reject-popover {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: 140px;
  background: white;
  box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  border-radius: 8px;
  padding: 4px 0;
  z-index: 100;
  margin-bottom: 12px;

  .reject-item {
    padding: 12px 0;
    font-size: 13px;
    color: #333;
    text-align: center;
    border-bottom: 1px solid #f7f8fa;

    &:last-child {
      border-bottom: none;
    }

    &:active {
      background: #f2f3f5;
    }
  }

  .popover-arrow {
    position: absolute;
    bottom: -6px;
    left: 50%;
    margin-left: -6px;
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid white;
  }
}

@media (max-width: 374px) {
  .section-header-simple {
    align-items: flex-start;
  }

  .refresh-btn {
    margin-top: -2px;
  }

  .strategy-card {
    .card-top-row {
      flex-direction: column;
      align-items: stretch;
    }

    .info-content-box {
      .info-row-flex {
        grid-template-columns: 1fr;
      }
    }
  }

  .accept-action-row {
    flex-wrap: wrap;
    justify-content: flex-end;
  }
}
</style>
